import * as buttons from 'smg/buttons';
import * as countries from 'smg/countries';
import { GetChildren, GetLineItems, GetSizes, GetTypes } from 'smg/customer-api/lineitems';
import * as models from 'smg/customer-api/models';
import * as smgPromo from 'smg/customer-api/promos';
import * as quoterequests from 'smg/customer-api/quoterequests';
import { Session } from 'smg/customer-api/session';
import { ShowError } from 'smg/errors';
import { BlockPastDates, ReportValidity } from 'smg/form';
import { CleanNumber } from 'smg/number';
import { Phone } from 'smg/phone';
import { Uploader } from 'smg/uploader/v2';
import { GetFirstAndLast } from 'smg/users';
import { Relref } from '../hugo';

declare interface myWindow extends Window {
    dataLayer: Record<string, unknown>[];
}
declare const window: myWindow;

void main();

async function main() {
    const $form = document.querySelector('#quote-form') as HTMLFormElement;
    const $fieldset = $form.querySelector('#quote-fieldset') as HTMLFieldSetElement;

    const $product = $form.querySelector('#quote-product') as HTMLSelectElement;
    const $type = $form.querySelector('#quote-type') as HTMLSelectElement;
    const $size = $form.querySelector('#quote-size') as HTMLSelectElement;
    const $childrenContainer = $form.querySelector('#quote-children') as HTMLElement;

    const $quantity = $form.querySelector('#quote-quantity') as HTMLInputElement;
    const $budget = $form.querySelector('#quote-budget') as HTMLInputElement;
    const $project = $form.querySelector('#quote-project') as HTMLInputElement;
    const $uploadZone = $form.querySelector('#quote-upload-zone') as HTMLElement;
    const $uploadThumbs = $form.querySelector('#quote-upload-thumbs') as HTMLElement;

    const $name = $form.querySelector('#quote-name') as HTMLInputElement;
    const $company = $form.querySelector('#quote-company') as HTMLInputElement;
    const $country = $form.querySelector('#quote-country') as HTMLSelectElement;
    const $zip = $form.querySelector('#quote-zip') as HTMLInputElement;
    const $phone = $form.querySelector('#quote-phone') as HTMLInputElement;
    const $email = $form.querySelector('#quote-email') as HTMLInputElement;

    const $dateNeeded = $form.querySelector('#quote-date-needed') as HTMLInputElement;
    // Tomorrow, not today — NewQuoteRequest's server floor rejects a
    // dateNeeded before tomorrow, and that 400 only surfaces as a toast.
    BlockPastDates($dateNeeded, { earliest: 'tomorrow' });
    const $notes = $form.querySelector('#quote-notes') as HTMLTextAreaElement;
    const $promo = $form.querySelector('#quote-promo') as HTMLInputElement;

    const $submitBtn = $form.querySelector('#quote-submit') as HTMLButtonElement;

    const phoneInput = new Phone($phone);
    void countries.Selects($country, undefined, {
        defaultUnitedStates: true,
        phone: phoneInput,
    });

    const promoInput = new smgPromo.Input($promo, { $email });

    const $uploadInput = $uploadZone.querySelector('input[type="file"]') as HTMLInputElement;
    const u = new Uploader({
        $input: $uploadInput,
        $dropzones: [$uploadZone],
        $submitButtons: [$submitBtn],
        $thumbnailsParent: $uploadThumbs,
    });
    $uploadZone.addEventListener('click', () => $uploadInput.click());

    const products = await GetLineItems();
    for (const p of products) {
        if (p.networkLineItem?.enabled) {
            $product.appendChild(option(p.networkNamePlural()));
        }
    }

    $product.addEventListener('change', () => {
        void onProductChange();
    });
    $type.addEventListener('change', () => {
        void onTypeChange();
    });

    async function onProductChange() {
        resetSelect($type, 'Select type');
        resetSelect($size, 'Select size');
        $type.disabled = true;
        $size.disabled = true;
        $childrenContainer.innerHTML = '';

        const product = $product.value;
        if (!product) {
            return;
        }

        $quantity.value = defaultQuantity(product);

        const types = await GetTypes(product);
        for (const t of types) {
            if (t.networkLineItemType?.enabled) {
                $type.appendChild(option(t.networkNamePlural()));
            }
        }
        $type.disabled = false;

        const children = await GetChildren(product);
        const sortOrder = ['Plating', 'Attachment', 'Presentation & Packaging', 'Epoxy Dome', 'Border', 'Edge'];
        const filtered = children.
            filter(c => c.networkChildLineItem?.quoteForm && c.childLineItemOptions.some(o => o.networkChildLineItemOption?.enabled)).
            sort((a, b) => {
                const ai = sortOrder.indexOf(a.name);
                const bi = sortOrder.indexOf(b.name);
                if (ai === -1 && bi === -1) {
                    return 0;
                }
                if (ai === -1) {
                    return 1;
                }
                if (bi === -1) {
                    return -1;
                }
                return ai - bi;
            });

        for (const c of filtered) {
            $childrenContainer.appendChild(buildChildSelect(c));
        }
    }

    async function onTypeChange() {
        resetSelect($size, 'Select size');
        $size.disabled = true;

        const product = $product.value;
        const type = $type.value;
        if (!product || !type) {
            return;
        }

        const sizes = await GetSizes(product, type);
        for (const s of sizes) {
            if (s.networkLineItemTypeSize?.enabled) {
                $size.appendChild(option(s.networkName()));
            }
        }
        $size.disabled = false;
    }

    function buildChildSelect(c: models.ChildLineItem): HTMLElement {
        const $wrapper = document.createElement('div');
        $wrapper.className = 'mb-3';

        const $label = document.createElement('label');
        $label.className = 'form-label';
        $label.htmlFor = `quote-child-${c.id}`;
        $label.innerHTML = `What ${c.networkName().toLowerCase()} would you like? <span class="text-danger">*</span>`;
        $wrapper.appendChild($label);

        const $select = document.createElement('select');
        $select.id = `quote-child-${c.id}`;
        $select.name = c.networkName();
        $select.className = 'form-control form-select';
        $select.required = true;
        const $placeholder = document.createElement('option');
        $placeholder.value = '';
        $placeholder.textContent = `Select ${c.networkName()}`;
        $select.appendChild($placeholder);
        for (const o of c.childLineItemOptions) {
            if (o.networkChildLineItemOption?.enabled) {
                $select.appendChild(option(o.networkName()));
            }
        }
        $wrapper.appendChild($select);

        return $wrapper;
    }

    $form.addEventListener('submit', async e => {
        e.preventDefault();

        await promoInput.check();

        if (!ReportValidity($form)) {
            return;
        }

        $fieldset.disabled = true;
        buttons.Start($submitBtn);

        const $childSelects = Array.from($childrenContainer.querySelectorAll<HTMLSelectElement>('select'));
        const children: models.QuoteRequestCreateChild[] = $childSelects.map($s => new models.QuoteRequestCreateChild({
            child: $s.name,
            option: $s.value,
        }));

        const quantity = parseInt(CleanNumber($quantity.value), 10) || 0;
        const budget = parseFloat(CleanNumber($budget.value)) || undefined;
        const cID = parseInt($country.value, 10) || undefined;

        // Awaited outside the chain — a PUT-failure throw would otherwise skip
        // the catch/finally below and leave the form disabled.
        let uploadIDs: number[];
        try {
            uploadIDs = await u.uploadIDs();
        } catch (err) {
            ShowError(err);
            $fieldset.disabled = false;
            buttons.Stop($submitBtn);
            return;
        }

        await quoterequests.NewQuoteRequest({
            form: document.body.id === 'iframe-quote-form' ? 'iframe quote form' : 'quote',
            name: $name.value,
            company: $company.value,
            phone: await phoneInput.format(),
            email: $email.value,
            countryID: cID,
            zip: $zip.value || undefined,
            lineItem: $product.value,
            type: $type.value,
            size: $size.value,
            children,
            quantity,
            budget,
            project: $project.value,
            dateNeeded: $dateNeeded.value || '',
            notes: $notes.value,
            uploadIDs,
            promo: $promo.value || undefined,
        }).then(async resp => {
            const sess = await Session().catch(() => undefined);
            const { firstName, lastName } = GetFirstAndLast($name.value);
            const dataLayer = window.dataLayer || [];
            dataLayer.push({
                'event': 'ccn_hugo_quote_submission',
                'userData': {
                    'email': sess?.user.emailAddress.email || $email.value,
                    'phone': sess?.user.phoneNumber.phone || await phoneInput.format(),
                    'firstName': sess?.user.firstName || firstName,
                    'lastName': sess?.user.lastName || lastName,
                },
                'transactionId': resp.id,
                'total': resp.quotes?.[0].cachedTotal,
            });

            if (document.body.id === 'iframe-quote-form') {
                $form.replaceWith(buildIframeSuccess());
            } else {
                window.location.href = `${Relref('/quote-success')}?id=${resp.quotes?.[0].key || ''}`;
            }
        }).catch(err => {
            ShowError(err);
            $fieldset.disabled = false;
            buttons.Stop($submitBtn);
        });
    });
}

function option(label: string): HTMLOptionElement {
    const $o = document.createElement('option');
    $o.textContent = label;
    $o.value = label;
    return $o;
}

function resetSelect($select: HTMLSelectElement, placeholder: string) {
    $select.innerHTML = '';
    const $o = document.createElement('option');
    $o.value = '';
    $o.textContent = placeholder;
    $select.appendChild($o);
}

function buildIframeSuccess(): HTMLElement {
    const $div = document.createElement('div');
    $div.className = 'alert alert-success my-3';
    $div.setAttribute('role', 'alert');
    $div.innerHTML = `
        <h5 class="alert-heading mb-1">Quote request received</h5>
        <p class="mb-0">Thanks! A representative will be in touch shortly.</p>
    `;
    return $div;
}

function defaultQuantity(product: string): string {
    switch (product) {
    case 'Patches':
    case 'Lapel Pins':
    case 'Buttons':
    case 'Charms':
    case 'Key Chains':
    case 'Lanyards':
        return '100';
    default:
        return '50';
    }
}
